import { useQuery } from '@tanstack/react-query';
import { createContext, ReactNode, useContext } from 'react';
import { authClient } from '../../lib/auth-client';

async function checkAuth() {
    const session = await authClient.getSession();

    if (!session.data?.user) {
        return { isLoggedIn: false, roles: [] as string[] };
    }

    return {
        isLoggedIn: true,
        roles: (session.data.user as any).roles ?? [],
    };
}

interface AuthContextType {
    isLoggedIn?: boolean;
    roles?: string[];
    recheckAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const { data: authStatus, refetch } = useQuery({
        queryKey: ['authStatus'],
        queryFn: checkAuth,
    });

    const recheckAuth = async () => {
        refetch();
    };

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn: authStatus?.isLoggedIn,
                roles: authStatus?.roles,
                recheckAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useIsLoggedIn() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useIsLoggedIn must be used within an AuthProvider');
    }

    return {
        isLoggedIn: context.isLoggedIn,
        roles: context.roles,
        recheckAuth: context.recheckAuth,
    };
}

export async function logout() {
    await authClient.signOut();
}
